import React, { useState, useCallback, useMemo } from 'react';
import { Platform, View } from 'react-native';
import { Image as ExpoImage } from 'expo-image';
import { SvgUri } from 'react-native-svg';
import { isSvgImageSource } from '@/constants/utils/svg_image';
import { resolveFullSourceUri, resolvePreviewUri } from '@/constants/utils/media_preview';
import { useOnScreen } from '@/hooks/useOnScreen';

function mapContentFitToObjectFit(contentFit) {
  if (contentFit === 'cover') return 'cover';
  if (contentFit === 'fill') return 'fill';
  return 'contain';
}

function mapContentFitToPreserveAspectRatio(contentFit) {
  if (contentFit === 'cover') return 'xMidYMid slice';
  if (contentFit === 'fill') return 'none';
  return 'xMidYMid meet';
}

function usesWebImageTag() {
  // Only web has a DOM. macOS is a native platform and must use SvgUri;
  // rendering <img> there throws "View config getter callback for component `img`".
  return Platform.OS === 'web';
}

function MediaImagePreview({
  uri,
  file = null,
  style,
  contentFit = 'cover',
  placeholder,
  transition,
  preferThumbnail = false,
  lazy = true,
  onLoadStart,
  onLoadEnd,
  onError,
}) {
  const [hostRef, isOnScreen] = useOnScreen({ eager: !lazy });
  const vectorUri = resolveFullSourceUri(uri, file);
  const isSvg = isSvgImageSource(file || vectorUri);
  const [layout, setLayout] = useState({ width: 0, height: 0 });
  const [useThumbnailFallback, setUseThumbnailFallback] = useState(false);

  const previewUri = useMemo(
    () => resolvePreviewUri({ uri, file, preferThumbnail, isSvg, useThumbnailFallback }),
    [uri, file, preferThumbnail, isSvg, useThumbnailFallback],
  );

  const handleLayout = useCallback((event) => {
    const { width, height } = event.nativeEvent.layout;
    setLayout({ width, height });
  }, []);

  const handleImageError = useCallback((event) => {
    if (isSvg && file?.thumbnailPath && !useThumbnailFallback) {
      setUseThumbnailFallback(true);
      return;
    }
    onError?.(event);
  }, [isSvg, file?.thumbnailPath, useThumbnailFallback, onError]);

  let inner = null;
  if (isOnScreen && previewUri) {
    if (isSvg && !useThumbnailFallback) {
      if (usesWebImageTag()) {
        const objectFit = mapContentFitToObjectFit(contentFit);
        inner = (
          <img
            src={previewUri}
            alt=""
            onLoad={(event) => {
              onLoadStart?.();
              onLoadEnd?.(event);
            }}
            onError={handleImageError}
            style={{
              width: '100%',
              height: '100%',
              objectFit,
              display: 'block',
            }}
          />
        );
      } else if (layout.width > 0 && layout.height > 0) {
        inner = (
          <SvgUri
            uri={previewUri}
            width={layout.width}
            height={layout.height}
            preserveAspectRatio={mapContentFitToPreserveAspectRatio(contentFit)}
            onError={handleImageError}
          />
        );
      }
    } else {
      inner = (
        <ExpoImage
          source={{ uri: previewUri }}
          style={{ width: '100%', height: '100%' }}
          contentFit={contentFit}
          placeholder={placeholder}
          transition={transition}
          recyclingKey={previewUri}
          cachePolicy={preferThumbnail ? 'disk' : 'memory-disk'}
          onLoadStart={onLoadStart}
          onLoadEnd={onLoadEnd}
          onError={onError}
        />
      );
    }
  }

  return (
    <View ref={hostRef} style={style} onLayout={handleLayout}>
      {inner}
    </View>
  );
}

export default React.memo(MediaImagePreview);
